import { Container, Layout, Loader } from "@certn/cascadia-react";
import { APIError, useAPIRequest } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import * as Sentry from "@sentry/browser";
import { FormikProps, FormikValues } from "formik";
import { createContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  CheckEngineAPIServerError,
  CheckEngineAPIValidationError,
  CheckEngineValidationError,
} from "./errors";
import { useApplicationSections } from "./hooks";
import { AppFooter } from "../../components/AppFooter";
import { AppHeader } from "../../components/AppHeader";
import { InaccessibleApplication } from "../../components/InaccessibleApplication";

export type Section = components["schemas"]["Section"] & {
  isCompleted: boolean;
  isDisabled: boolean;
  unsavedValues?: object;
};

type ApplicationContextProps = {
  sections: Section[];
  currentSectionKey?: string;
  formikRef: React.RefObject<FormikProps<FormikValues>>;
  saveData: ({
    sectionKey,
    skipValidation,
    hasMultipleEntries,
  }: {
    sectionKey?: string;
    skipValidation?: boolean;
    hasMultipleEntries?: boolean;
  }) => Promise<void>;
  completeSection: () => void;
  uncompleteSection: (section?: string) => void;
};

export const ApplicationContext = createContext<ApplicationContextProps>({
  sections: [],
  formikRef: { current: null },
  saveData: async () => {},
  completeSection: () => {},
  uncompleteSection: (section) => {},
});

function parseSections(
  sections: components["schemas"]["Section"][] = [],
  completed: string[] = [],
  unsavedValues: Record<string, object | undefined>
) {
  return sections.map((section, index, array) => {
    const prevSections = array.slice(0, index);

    const isCompleted = completed.includes(section.key);

    const previousSectionRequiredAndIncomplete = prevSections.some(
      (prevSection) =>
        prevSection.required && !completed.includes(prevSection.key)
    );

    return {
      ...section,
      isCompleted,
      isDisabled: previousSectionRequiredAndIncomplete,
      unsavedValues: unsavedValues[section.key],
    };
  });
}

export function ApplicationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  const { applicantId, sectionName } = useParams();

  const localStorageSectionsKey = `sections-${applicantId}`;

  const [completedSections, setCompletedSections] = useState<string[]>(
    JSON.parse(
      localStorage.getItem(localStorageSectionsKey + "-completed") || "[]"
    )
  );
  const [unsavedValues, setUnsavedValues] = useState<
    Record<string, object | undefined>
  >(
    JSON.parse(
      sessionStorage.getItem(localStorageSectionsKey + "-unsaved") || "{}"
    )
  );

  const formikRef = useRef<FormikProps<FormikValues>>(null);

  const { data, error, isLoading } = useApplicationSections();

  const request = useAPIRequest();

  useEffect(() => {
    localStorage.setItem(
      localStorageSectionsKey + "-completed",
      JSON.stringify(completedSections)
    );
  }, [completedSections]);

  useEffect(() => {
    localStorage.setItem(
      localStorageSectionsKey + "-unsaved",
      JSON.stringify(unsavedValues)
    );
  }, [unsavedValues]);

  const updateUnsavedValues = (
    section: string | undefined,
    values: object | undefined
  ) => {
    if (!section) {
      return;
    }

    setUnsavedValues((curr) => ({
      ...curr,
      [section]: values,
    }));
  };

  const saveData = async ({
    sectionKey = currentSectionKey,
    skipValidation = false,
    hasMultipleEntries = false,
  }) => {
    if (!formikRef.current) {
      return;
    }

    formikRef.current.setStatus();

    // sections that let you create multiple entries like Addresses saves data after each addition + deletion
    // formik validation happens during helpers.setValue so this would call it twice on each change
    if (!hasMultipleEntries) await formikRef.current.validateForm();

    if (!skipValidation && !formikRef.current.isValid) {
      uncompleteSection();
      updateUnsavedValues(sectionKey, formikRef.current.values);
      throw new CheckEngineValidationError();
    }

    // make a separate request for each field
    // this ensures that if one field has errors, it won't prevent other fields from updating
    const results = await Promise.allSettled(
      Object.entries(formikRef.current.values).map(([key, value]) =>
        request(`/internal/people/applicant/${applicantId}/update/`, {
          method: "POST",
          body: JSON.stringify({ [key]: value }),
        })
      )
    );

    const errors: Error[] = results
      .map((result) => result.status === "rejected" && result.reason)
      .filter(Boolean);

    const validationErrors = errors.filter(
      (error) => error instanceof APIError && error.status === 400
    ) as APIError[];

    if (errors.length > validationErrors.length) {
      uncompleteSection();
      updateUnsavedValues(sectionKey, formikRef.current.values);
      const error = new CheckEngineAPIServerError(
        errors,
        `API errors on ${sectionKey} section: ${errors
          .map(({ message }) => message)
          .join("; ")}`
      );
      Sentry.captureException(error);
      throw error;
    }

    const fieldErrors = errors.reduce((errors, error) => {
      if (error instanceof APIError) {
        return { ...errors, ...error.errors };
      }

      return {
        ...errors,
        status: t("Something went wrong, try again"),
      };
    }, formikRef.current.errors || {});

    formikRef.current.setErrors(fieldErrors);

    if (errors.length > 0) {
      uncompleteSection();
      updateUnsavedValues(sectionKey, formikRef.current.values);
      throw new CheckEngineAPIValidationError(
        errors,
        `API validation errors on ${sectionKey} section: ${JSON.stringify(
          validationErrors.map(({ errors }) => errors)
        )}`
      );
    }

    updateUnsavedValues(sectionKey, undefined);

    if (!hasMultipleEntries) completeSection();
  };

  const completeSection = (section = currentSectionKey) => {
    if (!section) {
      return;
    }

    setCompletedSections((sections) => [...new Set(sections), section]);
  };

  const uncompleteSection = (section = currentSectionKey) => {
    setCompletedSections((sections) =>
      sections.filter((sectionKey) => sectionKey !== section)
    );
  };

  if (isLoading) {
    return <Loader data-testid="loader" overlay />;
  }

  if (error) {
    return (
      <Layout
        layout="applicant/narrow"
        header={<AppHeader />}
        footer={<AppFooter />}
      >
        <Container>
          <InaccessibleApplication />
        </Container>
      </Layout>
    );
  }

  const sections = parseSections(data, completedSections, unsavedValues);
  const currentSectionKey = sectionName;

  return (
    <ApplicationContext.Provider
      value={{
        sections,
        formikRef,
        saveData,
        currentSectionKey,
        completeSection,
        uncompleteSection,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
}
